/**
 * One labelled track toggle. Shows the label and nothing else: no
 * channel numbers, no mono/stereo indication, no meters. Click or
 * Enter toggles; a long press solos.
 */
const LONG_PRESS_MS = 500;

export class TrackChip extends HTMLElement {
  private button: HTMLButtonElement | null = null;
  private pressTimer: number | null = null;
  private longPressFired = false;

  connectedCallback(): void {
    const label = this.getAttribute('label') ?? '';
    const button = document.createElement('button');
    button.className = 'chip';
    button.type = 'button';
    button.textContent = label;
    button.setAttribute('aria-pressed', this.getAttribute('active') === 'true' ? 'true' : 'false');
    this.button = button;
    this.replaceChildren(button);

    button.addEventListener('pointerdown', () => {
      this.longPressFired = false;
      this.pressTimer = window.setTimeout(() => {
        this.longPressFired = true;
        this.dispatchEvent(new CustomEvent('track-solo', { bubbles: true }));
      }, LONG_PRESS_MS);
    });
    const cancelPress = () => {
      if (this.pressTimer !== null) {
        window.clearTimeout(this.pressTimer);
        this.pressTimer = null;
      }
    };
    button.addEventListener('pointerup', cancelPress);
    button.addEventListener('pointerleave', cancelPress);
    button.addEventListener('pointercancel', cancelPress);

    // Suppress the click that follows a completed long press so a solo
    // is never immediately undone by the synthetic click event.
    button.addEventListener('click', (event) => {
      if (this.longPressFired) {
        event.stopImmediatePropagation();
        this.longPressFired = false;
        return;
      }
      this.dispatchEvent(new CustomEvent('track-toggle', { bubbles: true }));
    });

    // A long press on touch devices must not open the context menu.
    button.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  setActive(on: boolean): void {
    this.button?.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  /**
   * A chip is disabled until its track has decoded: an unready channel
   * cannot be armed, and the dimmed face says so honestly instead of
   * toggling into silence.
   */
  setReady(on: boolean): void {
    this.button?.toggleAttribute('disabled', !on);
  }
}

customElements.define('eternally-track-chip', TrackChip);
